import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {MDBIcon, MDBTooltip} from "mdb-react-ui-kit";

const Header = ({ isAuthenticated, isAdmin, onLogout }) => {
    const [isPosterOpen, setIsPosterOpen] = useState(false);

    const dropdownRef = useRef(null);

    // 🟦 세션 만료 타이머
    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        let timerId;
        function updateTimeLeft() {
            const exp = Number(localStorage.getItem("auth_token_exp"));
            if (!exp) {
                setTimeLeft(null);
                return;
            }
            const now = Date.now();
            const diff = Math.max(0, exp - now);
            setTimeLeft(diff);

            if (diff > 0) {
                timerId = setTimeout(updateTimeLeft, 1000);
            }
        }
        updateTimeLeft();
        return () => timerId && clearTimeout(timerId);
    }, []);

    // 🟦 연장 버튼 클릭 핸들러 (Alert + API)
    const handleExtendSession = async () => {
        const ok = window.confirm("로그인 시간을 연장하시겠습니까?");
        if (!ok) return;

        try {
            // 예시: /auth/extend API가 연장된 exp 반환
            const token = localStorage.getItem("auth_token");
            const res = await fetch(`${process.env.REACT_APP_API_REAL_URL}/auth/extend`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-TOKEN": token
                }
            });

            const data = await res.json();
            if (data.expiration) {
                localStorage.setItem("auth_token_exp", data.expiration.toString());
                setTimeLeft(data.expiration - Date.now());
                alert("로그인 시간이 연장되었습니다.");
            } else {
                alert("세션 연장에 실패했습니다.");
            }
        } catch (e) {
            alert("세션 연장에 실패했습니다.");
        }
    };

    const handleLogout = () => {
        onLogout();
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsPosterOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // 남은 시간 포맷
    const renderTimeLeft = () => {
        if (timeLeft == null) return null;
        const totalSec = Math.floor(timeLeft / 1000);
        const min = Math.floor(totalSec / 60);
        const sec = totalSec % 60;
        return `${min}분 ${sec}초`;
    };

    return (
        <header className="fixed top-0 left-0 w-full z-50 bg-white border-b border-gray-200 shadow-sm">
            <div className="w-full py-3 flex justify-between items-center">
                {/* 왼쪽: 로고 + 메뉴 */}
                <div className="flex items-center gap-10" style={{ paddingLeft: "5%" }}>
                    <Link to="/">
                        <img src="/proposal/logo.png" alt="ONNME" width={150} />
                    </Link>

                    <ul className="flex gap-6 items-center text-sm font-medium text-gray-700 relative">
                        <li>
                            <Link to="/" className="hover:text-blue-600 transition">나라장터 검색</Link>
                        </li>

                        <li>
                            <a
                                href="/onandme/proposal/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-blue-600 transition"
                            >
                                온앤미 인포
                            </a>
                        </li>

                        <li className="relative" ref={dropdownRef}>
                            <div
                                className="hover:text-blue-600 transition cursor-pointer"
                                onClick={() => setIsPosterOpen(!isPosterOpen)}
                            >
                              업로드 ▼
                            </div>

                            {isPosterOpen && (
                                <ul className="absolute left-0 mt-2 w-52 bg-white border rounded shadow-lg z-50 text-sm py-2">

                                    <li>
                                        <a href="/onandme/proposal/upload" className="block px-4 py-2 hover:bg-gray-100">
                                            제안서 업로드
                                        </a>
                                    </li>

                                    <li>
                                        <a href="/onandme/upload/2d" className="block px-4 py-2 hover:bg-gray-100">
                                            키비주얼 업로드
                                        </a>
                                    </li>

                                    <li>
                                        <a href="/onandme/upload/3d" className="block px-4 py-2 hover:bg-gray-100">
                                            3D 업로드
                                        </a>
                                    </li>

                                    <li>
                                        <a href="/onandme/upload/event-venue/" className="block px-4 py-2 hover:bg-gray-100">
                                            행사장 업로드
                                        </a>
                                    </li>

                                </ul>
                            )}
                        </li>

                        <li>
                            <Link to="/qr-event-admin-list" className="hover:text-blue-600 transition">
                                QR-행사
                                <MDBTooltip tag="a" wrapperProps={{ href: "#" }} title="관계자만 접근할 수 있어요">
                                    <MDBIcon far icon="question-circle" />
                                </MDBTooltip>
                            </Link>
                        </li>

                        {isAdmin && (
                            <li>
                                <Link to="/employees" className="hover:text-blue-600 transition">직원 관리</Link>
                            </li>
                        )}
                    </ul>
                </div>

                {/* 오른쪽: 로그인/로그아웃 영역 */}
                <div className="flex items-center gap-3 text-sm pr-6">
                    {isAuthenticated ? (
                        <>

                            {/* 🟦 남은 시간 (클릭시 연장) */}
                            <span
                                className="text-blue-600 cursor-pointer select-none"
                                title="로그인 세션 남은 시간"
                                onClick={handleExtendSession}
                            >
                                {renderTimeLeft()}
                            </span>

                            <button
                                onClick={handleLogout}
                                className="text-red-500 hover:underline transition"
                            >
                                로그아웃
                            </button>
                            <a
                                href="/me"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-500"
                            >
                                내 정보(비밀번호 변경)
                            </a>

                        </>
                    ) : (
                        <span className="text-gray-400">로그인 필요</span>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;